<template>
  <Dialog modal dismissableMask header="Boards on site" position="top" v-model:visible="visible">
    <div ref="dialogBody">
      <ul style="display: flex; flex-direction: column; gap: 10px; margin-bottom: 45px;">
        <TransitionGroup>
          <li v-for="(board, boardIndex) in boardsOnSite" :key="board.key" style="display: flex; align-items: center; gap: 10px;">
            <i v-if="board.online.value" class="pi pi-wifi" style="width: 25px; padding: 5px; color: green;"></i>
            <i v-else class="pi pi-circle" style="width: 25px; padding: 5px; color: var(--gray-200);"></i>
            <InputText v-model.trim="board.boardName.value" style="width: 250px;"></InputText>
            <Button v-if="boardsOnSite.length > 1" severity="secondary" style="justify-content: center; width: 60px; height: 40px;" @click="onClickOrdinal($event, boardIndex)">
              <span v-html="ordinalLabel(boardIndex)"></span>
            </Button>
            <Button v-if="isRemovable(board)" severity="secondary" size="sm" style="justify-content: center; width: 60px; height: 40px;" @click="removeBoard(board)">
              <i class="pi pi-trash"></i>
            </Button>
            <div v-if="isAnyBoardRemovable" style="flex-grow: 1;"></div>
            <Checkbox v-if="!isThisBoard(board)"
              :binary="true"
              :style="{ marginLeft: isAnyBoardRemovable ? '0' : '5px' }"
              :modelValue="board.showUi.value"
              @update:modelValue="value => setShowUi(board, value)"
            ></Checkbox>
          </li>
        </TransitionGroup>
        <Menu ref="ordinalMenu" popup :model="ordinalMenuItems">
          <template #item="{item, props}">
            <a v-ripple v-bind="props.action">
              <span v-html="item.label"></span>
            </a>
          </template>
        </Menu>
        <li style="display: flex; align-items: center; gap: 10px;">
          <i style="width: 25px;"></i>
          <InputText placeholder="New board name" style="width: 250px;" @input="addBoard($event.target.value)"></InputText>
        </li>
      </ul>
      <div v-if="moreOptionsEnabled" class="submitOptions" @click="showingMoreOptions = true">
        <div v-if="!showingMoreOptions" style="text-align: center; font-size: small;">
          More options
        </div>
        <template v-else>
          <Checkbox v-model="shouldSubmitOrderingToAllBoards">
            Submit this ordering to entire site
          </Checkbox>
          <Checkbox v-model="shouldSubmitDisplayConfigurationToAllBoards">
            Submit this display configuration to entire site
          </Checkbox>
        </template>
      </div>
      <div style="display: flex; align-items: center; gap: 10px; margin-top: 22px;">
        <Button style="justify-content: center; flex-grow: 1; flex-basis: 0;" @click="ok()">
          Ok
        </Button>
        <Button style="justify-content: center; flex-grow: 1; flex-basis: 0;" severity="secondary" @click="cancel()">
          Cancel
        </Button>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { promiseTimeout } from "@vueuse/core";
import { arrayMove, sortBy, swap } from "./utility.js";
import { isThisBoard, getBoardId, getBoardHostname } from "./appUtility.js";
import store from "./store.js";
import Tag from "./Tag.js";

let props = defineProps([
  "visible"
]);

let visible = defineModel("visible");

let boardsOnSite = ref(null);
watch(() => props.visible, () => {
  let system = store.sessions[0].system;
  boardsOnSite.value = system.boardsOnSite.children.map(board => board.$clone());
  boardsOnSite.value.forEach(board => board.key = Math.random());
});

function ordinalLabel(index) {
  let suffix = null;
  let ordinalIndex = index + 1;
  switch (ordinalIndex) {
    case 1: suffix = "st"; break;
    case 2: suffix = "nd"; break;
    case 3: suffix = "rd"; break;
    default: suffix = "th"; break;
  }
  return `${ordinalIndex}<sup>${suffix}</sup>`;
}

let ordinalMenu = ref();
let ordinalMenuItems = ref([]);
function onClickOrdinal(event, boardIndex) {
  if (boardsOnSite.value.length == 2) {
    swap(boardsOnSite.value, 0, 1);
  }
  else {
    ordinalMenuItems.value = boardsOnSite.value.map((board, index) => {
      return {
        label: ordinalLabel(index),
        command: () => arrayMove(boardsOnSite.value, boardIndex, index)
      };
    });
    ordinalMenu.value.toggle(event);
  }
}

let dialogBody = ref();
async function addBoard(boardName) {
  let boardTemplate = store.sessions[0].system.tagJsonTemplates.value.board;
  let board = new Tag(null, null, boardTemplate);
  board.key = Math.random();
  board.boardName.value = boardName;
  boardsOnSite.value.push(board);

  await promiseTimeout();
  dialogBody.value.querySelector("li:nth-last-child(2) input").focus();
  dialogBody.value.querySelector("li:last-child input").value = "";
}

function isRemovable(board) {
  if (board.location.value == "remote")
    return !store.sessions[0].system.pushConnected.value;
  else
    return !board.online.value;
}

function removeBoard(board) {
  boardsOnSite.value = boardsOnSite.value.filter(other => other != board);
}

function setShowUi(board, value) {
  board.showUi.value = value;
  if (!value) {
    showingMoreOptions.value = true;
    shouldSubmitDisplayConfigurationToAllBoards.value = false;
  }
}

let isAnyBoardRemovable = computed(() => boardsOnSite.value.some(board => !board.online.value));
let moreOptionsEnabled = computed(() => boardsOnSite.value.filter(board => board.online.value).length > 1);
let showingMoreOptions = ref(false);

let shouldSubmitOrderingToAllBoards = ref(false);
let shouldSubmitDisplayConfigurationToAllBoards = ref(false);

let fetchedBoardsOnSiteResults = [];
watch(() => props.visible, async () => {
  if (props.visible) {
    
    // Clear results from last time dialog was open, in case user clicks OK before all tasks complete.
    fetchedBoardsOnSiteResults = [];
    
    // Create an array of tasks whose results will be each board's 'boardsOnSite' configuration.
    let boardsOnSiteOnline = boardsOnSite.value.filter(board => board.online.value);
    let tasks = boardsOnSiteOnline.map(async (board) => {
      let nonRelativeBoardId = (board.boardId.value == "localhost") ? getBoardId(store.sessions[0].system.macAddr.value) : board.boardId.value;
      let session = store.sessions.find(session => {
        let sessionBoardId = session.system ? getBoardId(session.system.macAddr.value) : null;
        return sessionBoardId == nonRelativeBoardId;
      });
      if (session) {
        fetchedBoardsOnSiteResults.push({
          nonRelativeBoardId,
          boardName: board.boardName.value,
          boardsOnSite: session.system?.boardsOnSite.children ?? []
        });
      }
      else {
        let hostname = getBoardHostname(board);
        try {
          let response = await fetch(`http://${hostname}/cmd/getBoardsOnSite`, {
            mode: "cors",
            method: "POST",
            credentials: "include"
          });
          let responseBody = await response.json();
          fetchedBoardsOnSiteResults.push({
            nonRelativeBoardId,
            boardName: board.boardName.value,
            boardsOnSite: responseBody.children
          });
        }
        catch (error)
        {}
      }
    });

    // Wait for the tasks to complete, ignoring tasks that failed.
    (await Promise.all(tasks));

    // Decide whether "Submit this ordering to entire site" should be checked.
    // Should be checked if each board has the same ordering for it's copy of 'boardsOnSite'.
    let orderings = fetchedBoardsOnSiteResults.map(result => JSON.stringify(result.boardsOnSite.map(board => board.boardId.value ?? board.boardName.value)));
    shouldSubmitOrderingToAllBoards.value = orderings.every(ordering => ordering == orderings[0]);

    // Decide whether "Submit this display configuration to entire site" should be checked.
    // Should be checked if each board has the same display configuration for all boards in 'boardsOnSite'.
    let displayConfigurations = fetchedBoardsOnSiteResults.map(result => {
      let boardsOnSiteAlphabetical = sortBy(result.boardsOnSite, board => board.boardName.value);
      let displayConfiguration = boardsOnSiteAlphabetical.map(board => board.showUi.value);
      let displayConfigurationString = JSON.stringify(displayConfiguration);
      return displayConfigurationString;
    });
    shouldSubmitDisplayConfigurationToAllBoards.value = displayConfigurations.every(displayConfiguration => displayConfiguration == displayConfigurations[0]);

    showingMoreOptions.value = !shouldSubmitOrderingToAllBoards.value || !shouldSubmitDisplayConfigurationToAllBoards.value;
  }
});

async function ok() {
  visible.value = false;
  boardsOnSite.value.forEach(board => {
    if (!board.online.value) return;
    let primaryLocalhostId = getBoardId(store.sessions[0].system.macAddr.value);
    let nonRelativeBoardId = (board.boardId.value == "localhost") ? primaryLocalhostId : board.boardId.value;
    let fetchedBoardsOnSiteResult = fetchedBoardsOnSiteResults.find(result => result.nonRelativeBoardId == nonRelativeBoardId);
    let fetchedBoardsOnSite = fetchedBoardsOnSiteResult?.boardsOnSite;
    let sessionIndex = store.sessions.findIndex(session => {
      let sessionBoardId = session.system ? getBoardId(session.system.macAddr.value) : null;
      return sessionBoardId == nonRelativeBoardId;
    });
    let isPrimarySession = (sessionIndex == 0);
    let shouldUseNewOrdering = (shouldSubmitOrderingToAllBoards.value || isPrimarySession);
    let shouldSubmitDisplayConfiguration = (shouldSubmitDisplayConfigurationToAllBoards.value || isPrimarySession);
    if (fetchedBoardsOnSite) {
      let unmodifiedBoardData = store.sessions[0].system.boardsOnSite.children.find(unmodified => unmodified.boardId.value == board.boardId.value);
      let hostname = getBoardHostname(unmodifiedBoardData);
      let boardsOnSiteOrderingReference = (shouldUseNewOrdering) ? boardsOnSite.value : fetchedBoardsOnSite;
      let localhostBoardId = (shouldUseNewOrdering) ? primaryLocalhostId : fetchedBoardsOnSiteResult.nonRelativeBoardId; 
      let boardsOnSiteOrdered = sortBy(boardsOnSite.value, board => {
        let index = boardsOnSiteOrderingReference.findIndex(referenceBoard => {
          let boardNonRelativeBoardId = (board.boardId.value == "localhost") ? primaryLocalhostId : board.boardId.value;
          let referenceBoardNonRelativeBoardId = (referenceBoard.boardId.value == "localhost") ? localhostBoardId : referenceBoard.boardId.value;
          return boardNonRelativeBoardId == referenceBoardNonRelativeBoardId;
        });
        return (index >= 0) ? index : 0xff;
      });
      let submittedBoardsOnSite = boardsOnSiteOrdered.map(orderedBoard => {
        let fetchedBoard = fetchedBoardsOnSite.find(fetchedBoard => {
          let orderedBoardNonRelativeBoardId = (orderedBoard.boardId.value == "localhost") ? primaryLocalhostId : orderedBoard.boardId.value;
          let fetchedBoardNonRelativeBoardId = (fetchedBoard.boardId.value == "localhost") ? fetchedBoardsOnSiteResult.nonRelativeBoardId : fetchedBoard.boardId.value;
          return fetchedBoardNonRelativeBoardId == orderedBoardNonRelativeBoardId;
        });
        return {
          handle: fetchedBoard?.handle || null,
          boardName: orderedBoard.boardName.value,
          showUi: shouldSubmitDisplayConfiguration ? orderedBoard.showUi.value : undefined
        }
      });
      fetch(`http://${hostname}/cmd/setBoardsOnSite`, {
        mode: "cors",
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          boardName: board.boardName.value,
          boardsOnSite: submittedBoardsOnSite
        })
      });
    }
  });
}

function cancel() {
  visible.value = false;
}
</script>

<style scoped>
.v-move,
.v-enter-active,
.v-leave-active,
.p-inputtext {
  transition: all 0.25s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
.v-leave-active {
  position: absolute;
}

.submitOptions {
  display: flex;
  flex-direction: column;
  padding: 15px;
  gap: 10px;
  background: whitesmoke;
  border-radius: 10px;
}

details {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
}
details summary {
  padding-left: 5px;
}
details summary::marker {
  color: #555;
  margin-right: 10px;
}
details[open] summary {
  padding-bottom: 15px;
}
</style>